// Copyright (c) Microsoft. All rights reserved.

import { Aionu } from '../models/app/AIONU';
import { PluginAPI } from '../models/app/Plugin';
import { IPluginPromptCategoryList } from '../models/studio/Category';
import {
  IFileData,
  IinputOutput,
  IinputOutputSave,
  IPluginContentsSave,
  IOpenAPIItem,
  IPluginFileSave,
  IPluginPromptData,
  IPluginPromptList,
  IPluginPromptParam,
  IPluginPromptSave,
  RagContentsData,
} from '../models/studio/PluginPromptList';
import { UserData } from '../models/users/UsersState';
import { BaseService } from './BaseService';

export class PluginService extends BaseService {
  public getAgentAutocompleteAsync = async (query?: string, count?: number): Promise<PluginAPI[]> => {
    const qs = new URLSearchParams();
    if (typeof query === 'string') {
      qs.append('query', query);
    }
    if (typeof count === 'number') {
      qs.append('count', String(count));
    }

    const result = await this.getResponseAsync<PluginAPI[]>({
      commandPath: `api/agent/autocomplete`,
      method: 'GET',
      query: qs,
    });

    return result;
  };
  public getAllPluginAsync = async (): Promise<PluginAPI[]> => {
    const result = await this.getResponseAsync<PluginAPI[]>({
      commandPath: `api/user/plugin`,
    });

    return result;
  };

  /**
   * [API] - 카테고리 목록 조회
   * @returns
   */
  public getPluginCategoryAsync = async (): Promise<IPluginPromptCategoryList> => {
    const result = await this.getResponseAsync<IPluginPromptCategoryList>({
      commandPath: `api/studio/category/plugin`,
      method: 'GET',
    });

    return result;
  };

  /**
   * [API] - 플러그인 리스트 조회
   * @param obj
   * @returns
   */
  public getPluginListAsync = async (obj: IPluginPromptParam): Promise<IPluginPromptList> => {
    const query = new URLSearchParams();
    for (const key in obj) {
      const value = obj[key];
      if (value != null) {
        query.append(key, value.toString());
      }
    }

    const result = await this.getResponseAsync<IPluginPromptList>({
      commandPath: `api/studio/plugins`,
      query: query,
      method: 'GET',
    });

    return result;
  };

  /**
   * [API] - 플러그인 북마크(즐겨찾기)
   * @param companyCode
   * @returns
   */
  public getPluginBookmarkAsync = async (obj: IPluginPromptParam): Promise<IPluginPromptList> => {
    const query = new URLSearchParams();
    for (const key in obj) {
      const value = obj[key];
      if (value != null) {
        query.append(key, value.toString());
      }
    }

    const result = await this.getResponseAsync<IPluginPromptList>({
      commandPath: `/api/studio/plugins/bookmark`,
      query: query,
      method: 'GET',
    });

    return result;
  };

  /**
   * [API] - 플러그인 검색
   * @param obj
   * @returns
   */
  public getPluginSearchAsync = async (obj: IPluginPromptParam): Promise<IPluginPromptList> => {
    const query = new URLSearchParams();
    for (const key in obj) {
      const value = obj[key];
      if (value != null) {
        query.append(key, value.toString());
      }
    }

    const result = await this.getResponseAsync<IPluginPromptList>({
      commandPath: `/api/studio/plugins/search`,
      query: query,
      method: 'GET',
    });

    return result;
  };

  // public setPluginStateAsync = async (
  //     chatId: string,
  //     pluginName: string,
  //     accessToken: string,
  //     enabled: boolean,
  // ): Promise<void> => {
  //     await this.getResponseAsync(
  //         {
  //             commandPath: `chats/${chatId}/plugins/${pluginName}/${enabled}`,
  //             method: 'PUT',
  //         },
  //         accessToken,
  //     );
  // };

  /**
   * [API] - 내 플러그인 조회
   * @param obj
   * @returns
   */
  public getMyPluginListAsync = async (obj: IPluginPromptParam): Promise<IPluginPromptList> => {
    const query = new URLSearchParams();
    for (const key in obj) {
      const value = obj[key];
      if (value != null) {
        query.append(key, value.toString());
      }
    }

    const result = await this.getResponseAsync<IPluginPromptList>({
      commandPath: `api/studio/plugins/my`,
      query: query,
      method: 'GET',
    });

    return result;
  };

  /**
   * [API] - 플러그인 생성
   * @param obj
   * @returns
   */
  public postPluginAsync = async (obj: IPluginPromptData): Promise<IPluginPromptSave> => {
    const result = await this.getResponseAsync<IPluginPromptSave>({
      commandPath: `/api/studio/plugin`,
      method: 'POST',
      body: obj,
    });

    return result;
  };

  /**
   * [API] - 플러그인 삭제
   * @param obj
   * @returns
   */
  public deletePluginAsync = async (obj: IPluginPromptData): Promise<IPluginPromptData> => {
    const result = await this.getResponseAsync<IPluginPromptData>({
      commandPath: `/api/studio/plugin`,
      method: 'DELETE',
      body: { id: obj.id },
    });

    return result;
  };

  /**
   * [API] - 플러그인 상세 조회
   * @param obj
   * @returns
   */
  public getPluginAsync = async (pluginId: string): Promise<IPluginPromptData> => {
    const result = await this.getResponseAsync<IPluginPromptData>({
      commandPath: `/api/studio/plugin?pluginId=${pluginId}`,
      method: 'GET',
    });

    return result;
  };

  /**
   * [API] - 에이전트 선택 시 상세 조회 (조회 전용)
   */
  public getAgentAsync = async (pluginId: string): Promise<IPluginPromptData> => {
    const result = await this.getResponseAsync<IPluginPromptData>({
      commandPath: `/api/agent/${pluginId}`,
      method: 'GET',
    });

    return result;
  };

  /**
   * [API] - 플러그인 수정
   * @param obj
   * @returns
   */
  public putPluginAsync = async (obj: IPluginPromptData): Promise<IPluginPromptSave> => {
    const result = await this.getResponseAsync<IPluginPromptSave>({
      commandPath: `/api/studio/plugin`,
      method: 'POST',
      body: obj,
    });

    return result;
  };

  /**
   * [API] - input/output 등록
   * @param obj
   * @returns
   */
  public putRagContentAsync = async (obj: IinputOutput): Promise<IinputOutputSave> => {
    const result = await this.getResponseAsync<IinputOutputSave>({
      commandPath: `/api/studio/plugin/ragcontent`,
      method: 'POST',
      body: obj,
    });

    return result;
  };

  /**
   * [API] -  input/output 삭제 - 단건
   * @param obj
   * @returns
   */
  public deleteRagContentAsync = async (obj: IinputOutput): Promise<IinputOutputSave> => {
    const result = await this.getResponseAsync<IinputOutputSave>({
      commandPath: `/api/studio/plugin/ragcontent`,
      method: 'DELETE',
      body: obj,
    });

    return result;
  };
  /**
   * [API] -  input/output 삭제 - 다건
   * @param obj
   * @returns
   */
  public deleteRagContentsAsync = async (obj: IinputOutput[]): Promise<IinputOutput> => {
    const result = await this.getResponseAsync<IinputOutput>({
      commandPath: `/api/studio/plugin/ragcontents`,
      method: 'DELETE',
      body: obj,
    });

    return result;
  };

  /**
   * [API] - 플러그인 북마크(즐겨찾기) 수정
   * @param obj
   * @returns
   */
  public postPluginBookmarkAsync = async (pluginId: string): Promise<IPluginPromptData> => {
    const result = await this.getResponseAsync<IPluginPromptData>({
      commandPath: `/api/studio/bookmark/plugin`,
      method: 'POST',
      body: { id: pluginId },
    });

    return result;
  };

  /**
   * [API] - 플러그인 북마크(즐겨찾기) 삭제
   * @param obj
   * @returns
   */
  public deletePluginBookmarkAsync = async (pluginId: string): Promise<IPluginPromptData> => {
    const result = await this.getResponseAsync<IPluginPromptData>({
      commandPath: `/api/studio/bookmark/plugin`,
      method: 'DELETE',
      body: { id: pluginId },
    });

    return result;
  };

  /**
   * [API] - 오늘 등록 갯수
   * @param obj
   * @returns
   */
  public getPluginTodayCountAsync = async (createdStatus: number) => {
    const result = await this.getResponseAsync<IPluginPromptList>({
      commandPath: `/api/studio/plugins/today?createdStatus=${createdStatus}`,
      method: 'GET',
    });

    return result;
  };

  /**
   * [API] - 플러그인 파일업로드
   * @param obj - 객체에 customPluginId, notDownloadable, isDraft 포함
   * @param file - 업로드할 파일
   * @returns API 호출 결과
   */
  public postPluginRagFileAsync = async (
    obj: { customPluginId: string; notDownloadable?: boolean; isDraft?: boolean },
    file: File,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    const notDownloadable = obj.notDownloadable?.toString() ?? 'false';
    const isDraft = obj.isDraft?.toString() ?? 'false';

    const result = await this.getResponseAsync<IFileData>({
      commandPath: `/api/studio/plugin/ragfile?customPluginId=${obj.customPluginId}&notDownloadable=${notDownloadable}&isDraft=${isDraft}`,
      method: 'POST',
      body: formData, // FormData를 body로 전달
    });

    return result;
  };

  /**
   * [POST] - 플러그인 파일 삭제
   * @param customPluginId
   * @param documentId
   * @param isDraft
   * @returns API 호출 결과
   */
  public deletePluginRagFileAsync = async (customPluginId: string, documentId?: string) => {
    const result = await this.getResponseAsync<IPluginFileSave>({
      commandPath: `/api/studio/plugin/ragfile?customPluginId=${customPluginId}&documentId=${documentId}`,
      method: 'DELETE',
    });

    return result;
  };
  /**
   * [POST] - 데이터템플릿 등록 > 파일 업로드
   * @returns
   */
  public uploadTemplateFileAsync = async (file: File, customPluginId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const result = await this.getResponseAsync<RagContentsData>({
      commandPath: `/api/studio/plugin/ragcontentfile?customPluginId=${customPluginId}`,
      method: 'POST',
      body: formData, // FormData를 body로 전달
    });

    return result;
  };

  /**
   * [POST] - 데이터템플릿 등록 > 파일 업로드
   * @returns
   */
  public uploadTemplateAsync = async (file: File, customPluginId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const result = await this.getResponseAsync<IPluginContentsSave>({
      commandPath: `/api/studio/plugin/ragcontent?customPluginId=${customPluginId}`,
      method: 'POST',
      body: formData, // FormData를 body로 전달
    });

    return result;
  };

  /**
   * [POST] - 데이터템플릿 등록 > 저장
   * @returns
   */
  public saveTemplateFileAsync = async (contents: RagContentsData) => {
    const result = await this.getResponseAsync<IPluginContentsSave>({
      commandPath: `/api/studio/plugin/ragcontent`,
      method: 'POST',
      body: contents, // FormData를 body로 전달
    });

    return result;
  };

  /**
   * [POST] - 데이터템플릿 등록 > 학습
   * @returns
   */
  public embeddingTemplateFileAsync = async (contents: RagContentsData) => {
    const result = await this.getResponseAsync<IPluginContentsSave>({
      commandPath: `/api/studio/plugin/ragcontent/embedding`,
      method: 'POST',
      body: contents,
    });

    return result;
  };

  /**
   * [POST] - 데이터템플릿 등록 > 삭제
   * @returns
   */
  public deleteTemplateFileAsync = async (contents: RagContentsData) => {
    const result = await this.getResponseAsync<RagContentsData>({
      commandPath: `/api/studio/plugin/ragcontent`,
      method: 'DELETE',
      body: contents,
    });

    return result;
  };

  /**
   * [API] - 플러그인 임시저장
   * @param obj
   * @returns
   */
  public postPluginDraftAsync = async (obj: IPluginPromptData): Promise<IPluginPromptSave> => {
    const result = await this.getResponseAsync<IPluginPromptSave>({
      commandPath: `/api/studio/plugin/draft`,
      method: 'POST',
      body: obj,
    });
    return result;
  };

  /**
   * [API] - 임시저장된 상태에서 아무것도 변경되지 않고 상태만 등록으로 변경
   * @param obj
   * @returns
   */
  public postPluginStatusAsync = async (obj: IPluginPromptData): Promise<IPluginPromptSave> => {
    const result = await this.getResponseAsync<IPluginPromptSave>({
      commandPath: `/api/studio/plugin/Status`,
      method: 'PUT',
      body: obj,
    });

    return result;
  };

  /**
   * [API] - 관리자 조회
   * @returns
   */
  public getMembers = async (keyword: string): Promise<UserData[]> => {
    const query = new URLSearchParams();
    query.append('keyword', keyword);
    const result = await this.getResponseAsync<UserData[]>({
      commandPath: `/api/studio/members`,
      method: 'GET',
      query: query,
    });

    return result;
  };

  /**
   * [API] - OpenAPI 아이템 조회
   * @returns
   */
  public getOpenAPIAsync = async (openAPIId: string): Promise<IOpenAPIItem> => {
    const query = new URLSearchParams();
    query.append('openAPIId', openAPIId);
    const result = await this.getResponseAsync<IOpenAPIItem>({
      commandPath: `api/studio/plugin/openapi`,
      method: 'GET',
      query: query,
    });

    return result;
  };

  /**
   * [API] - aionu 에이전트 조회
   * @returns
   */
  public getAionuAsync = async (): Promise<Aionu[]> => {
    const result = await this.getResponseAsync<Aionu[]>({
      commandPath: `api/aionu/link_apps`,
      method: 'GET',
    });

    return result;
  };
}
